import React, { useState } from 'react';
import { SearchIcon, FilterIcon, ChevronDownIcon, ChevronUpIcon, MessageSquareIcon, PlusIcon, CheckCircleIcon, XIcon, FileTextIcon, LayoutIcon, TargetIcon, ChevronRightIcon } from 'lucide-react';
interface Goal {
  id: number;
  title: string;
  type: 'pride' | 'note' | 'visionBoard';
  category: string;
  progress: number;
  createdAt: string;
  lastUpdated: string;
  mentorFeedback: Array<{
    id: number;
    message: string;
    date: string;
  }>;
  visionBoardData?: {
    thumbnail: {
      background: string;
      elementCount: number;
      drawPathCount: number;
    };
  };
}
const MenteeGoals: React.FC = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedFilter, setSelectedFilter] = useState<'all' | 'inProgress' | 'completed'>('all');
  const [showGoalOptions, setShowGoalOptions] = useState(false);
  const [selectedGoal, setSelectedGoal] = useState<Goal | null>(null);
  const [showGoalDetails, setShowGoalDetails] = useState(false);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  // Sample data for demonstration
  const goals: Goal[] = [{
    id: 1,
    title: 'Build confidence in professional settings',
    type: 'pride',
    category: 'Personal Development',
    progress: 45,
    createdAt: '2023-09-15',
    lastUpdated: '2023-10-01',
    mentorFeedback: [{
      id: 1,
      message: 'Great progress on your speaking exercises!',
      date: '2023-09-25'
    }]
  }, {
    id: 2,
    title: 'Thoughts on coming out at work',
    type: 'note',
    category: 'Identity',
    progress: 100,
    createdAt: '2023-08-20',
    lastUpdated: '2023-08-20',
    mentorFeedback: []
  }, {
    id: 3,
    title: 'My Career Vision 2024',
    type: 'visionBoard',
    category: 'Career',
    progress: 75,
    createdAt: '2023-10-05',
    lastUpdated: '2023-10-10',
    mentorFeedback: [{
      id: 1,
      message: "I love how you've visualized your 5-year plan!",
      date: '2023-10-07'
    }, {
      id: 2,
      message: 'Have you considered adding specific milestones?',
      date: '2023-10-09'
    }],
    visionBoardData: {
      thumbnail: {
        background: 'gradient',
        elementCount: 5,
        drawPathCount: 2
      }
    }
  }];
  const filteredGoals = goals.filter(goal => {
    const matchesSearch = goal.title.toLowerCase().includes(searchQuery.toLowerCase());
    if (selectedFilter === 'all') return matchesSearch;
    if (selectedFilter === 'completed') return matchesSearch && goal.progress === 100;
    if (selectedFilter === 'inProgress') return matchesSearch && goal.progress < 100;
    return matchesSearch;
  });
  const handleGoalTypeSelect = (type: 'pride' | 'note' | 'visionBoard') => {
    console.log(`Creating a new ${type} goal`);
    // Logic to open the appropriate goal creation modal/form
    setShowGoalOptions(false);
  };
  const GoalDetailsModal = () => {
    if (!selectedGoal) return null;
    return <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50" data-id="element-1106">
        <div className="bg-white rounded-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto" data-id="element-1107">
          <div className="p-6" data-id="element-1108">
            <div className="flex justify-between items-start mb-6" data-id="element-1109">
              <div data-id="element-1110">
                <h3 className="text-xl font-semibold" data-id="element-1111">{selectedGoal.title}</h3>
                <p className="text-sm text-gray-600" data-id="element-1112">{selectedGoal.category}</p>
              </div>
              <button onClick={() => setShowGoalDetails(false)} data-id="element-1113">
                <XIcon size={20} className="text-gray-500" data-id="element-1114" />
              </button>
            </div>
            {/* Goal details content would go here */}
            <div className="mb-6" data-id="element-1115">
              <div className="flex justify-between items-center mb-2" data-id="element-1116">
                <span className="text-sm font-medium" data-id="element-1117">Progress</span>
                <span className="text-sm font-medium text-purple-600" data-id="element-1118">
                  {selectedGoal.progress}%
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2" data-id="element-1119">
                <div className="bg-purple-600 rounded-full h-2 transition-all duration-300" style={{
                width: `${selectedGoal.progress}%`
              }} data-id="element-1120" />
              </div>
              <p className="text-sm text-gray-600 mt-2" data-id="element-1121">
                Last updated:{' '}
                {new Date(selectedGoal.lastUpdated).toLocaleDateString()}
              </p>
            </div>
            <div className="flex justify-end space-x-3 mt-6" data-id="element-1122">
              <button onClick={() => {
              setShowGoalDetails(false);
              setShowDeleteConfirmation(true);
            }} className="px-4 py-2 border border-red-300 text-red-600 rounded-lg hover:bg-red-50" data-id="element-1123">
                Delete Goal
              </button>
              <button onClick={() => setShowGoalDetails(false)} className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700" data-id="element-1124">
                Close
              </button>
              <button onClick={() => setShowGoalDetails(false)} className="px-4 py-2 bg-purple-600 text-white rounded-lg" data-id="element-1125">
                Edit Goal
              </button>
            </div>
          </div>
        </div>
      </div>;
  };
  const DeleteConfirmationModal = () => {
    if (!selectedGoal) return null;
    return <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50" data-id="element-1126">
        <div className="bg-white rounded-xl w-full max-w-md" data-id="element-1127">
          <div className="p-6" data-id="element-1128">
            <h3 className="text-lg font-semibold mb-2" data-id="element-1129">Delete Goal</h3>
            <p className="text-gray-600 mb-4" data-id="element-1130">
              Are you sure you want to delete "{selectedGoal.title}"? This
              action cannot be undone.
            </p>
            <div className="flex justify-end space-x-3" data-id="element-1131">
              <button onClick={() => setShowDeleteConfirmation(false)} className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700" data-id="element-1132">
                Cancel
              </button>
              <button onClick={() => {
              console.log(`Deleting goal ${selectedGoal.id}`);
              setShowDeleteConfirmation(false);
              setSelectedGoal(null);
            }} className="px-4 py-2 bg-red-600 text-white rounded-lg" data-id="element-1133">
                Delete
              </button>
            </div>
          </div>
        </div>
      </div>;
  };
  return <div className="pb-4" data-id="element-1134">
      <div className="mb-6" data-id="element-1135">
        <div className="flex justify-between items-center mb-2" data-id="element-1136">
          <h1 className="text-xl font-bold" data-id="element-1137">Your Goals</h1>
          <button onClick={() => setShowGoalOptions(!showGoalOptions)} className="px-4 py-2 bg-purple-600 text-white rounded-lg flex items-center hover:bg-purple-700 transition-colors" data-id="element-1138">
            <PlusIcon size={18} className="mr-1" data-id="element-1139" />
            Add Goal
          </button>
        </div>
        <p className="text-gray-600" data-id="element-1140">
          Track and manage your mentorship goals here.
        </p>
      </div>

      {/* Expandable Goal Options */}
      {showGoalOptions && <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6 animate-fade-in" data-id="element-1141">
          <div className="bg-purple-50 p-4 rounded-xl" data-id="element-1142">
            <div className="mb-3" data-id="element-1143">
              <h3 className="font-semibold text-lg" data-id="element-1144">PRIDE Framework Goals</h3>
              <p className="text-sm text-gray-600" data-id="element-1145">
                Structured goal-setting with clear steps and evaluation
              </p>
            </div>
            <button onClick={() => handleGoalTypeSelect('pride')} className="w-full py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors flex items-center justify-center" data-id="element-1146">
              <TargetIcon size={16} className="mr-2" data-id="element-1147" />
              Create PRIDE Goal
            </button>
          </div>
          <div className="bg-blue-50 p-4 rounded-xl" data-id="element-1148">
            <div className="mb-3" data-id="element-1149">
              <h3 className="font-semibold text-lg" data-id="element-1150">Open Note Memo</h3>
              <p className="text-sm text-gray-600" data-id="element-1151">
                Flexible journal-style format for thoughts and reflections
              </p>
            </div>
            <button onClick={() => handleGoalTypeSelect('note')} className="w-full py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center" data-id="element-1152">
              <FileTextIcon size={16} className="mr-2" data-id="element-1153" />
              Create Memo
            </button>
          </div>
          <div className="bg-green-50 p-4 rounded-xl" data-id="element-1154">
            <div className="mb-3" data-id="element-1155">
              <h3 className="font-semibold text-lg" data-id="element-1156">Vision Board</h3>
              <p className="text-sm text-gray-600" data-id="element-1157">
                Visual goal-setting with images, quotes, and inspiration
              </p>
            </div>
            <button onClick={() => handleGoalTypeSelect('visionBoard')} className="w-full py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center justify-center" data-id="element-1158">
              <LayoutIcon size={16} className="mr-2" data-id="element-1159" />
              Create Vision Board
            </button>
          </div>
        </div>}

      <div className="flex items-center space-x-4 mb-6" data-id="element-1160">
        <div className="flex-1 relative" data-id="element-1161">
          <SearchIcon size={20} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" data-id="element-1162" />
          <input type="text" placeholder="Search goals..." className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} data-id="element-1163" />
        </div>
        <select className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500" value={selectedFilter} onChange={e => setSelectedFilter(e.target.value as 'all' | 'inProgress' | 'completed')} data-id="element-1164">
          <option value="all" data-id="element-1165">All Goals</option>
          <option value="inProgress" data-id="element-1166">In Progress</option>
          <option value="completed" data-id="element-1167">Completed</option>
        </select>
      </div>

      <div className="space-y-4" data-id="element-1168">
        {filteredGoals.map(goal => <div key={goal.id} className="bg-white rounded-xl shadow-sm overflow-hidden cursor-pointer hover:shadow-md transition-shadow" onClick={() => {
        setSelectedGoal(goal);
        setShowGoalDetails(true);
      }} data-id="element-1169">
            <div className="p-4" data-id="element-1170">
              <div className="flex justify-between items-start mb-3" data-id="element-1171">
                <div data-id="element-1172">
                  <h3 className="font-semibold" data-id="element-1173">{goal.title}</h3>
                  <div className="flex items-center mt-1" data-id="element-1174">
                    <span className="text-xs bg-purple-100 text-purple-600 px-2 py-0.5 rounded-full" data-id="element-1175">
                      {goal.category}
                    </span>
                    <span className="text-xs ml-2 text-gray-500" data-id="element-1176">
                      Created: {new Date(goal.createdAt).toLocaleDateString()}
                    </span>
                  </div>
                </div>
                <div className="flex items-center" data-id="element-1177">
                  {goal.progress === 100 ? <span className="flex items-center text-green-600 text-sm" data-id="element-1178">
                      <CheckCircleIcon size={16} className="mr-1" data-id="element-1179" />
                      Completed
                    </span> : <span className="text-sm font-medium" data-id="element-1180">
                      {goal.progress}%
                    </span>}
                </div>
              </div>
              <div className="mb-3" data-id="element-1181">
                <div className="w-full bg-gray-200 rounded-full h-2" data-id="element-1182">
                  <div className={`rounded-full h-2 ${goal.progress === 100 ? 'bg-green-500' : 'bg-purple-600'}`} style={{
                width: `${goal.progress}%`
              }} data-id="element-1183" />
                </div>
              </div>
              {goal.type === 'visionBoard' && <div className="text-sm" data-id="element-1184">
                  <div className="h-[60px] bg-gray-100 rounded-lg flex items-center justify-center mb-2" data-id="element-1185">
                    <div className="text-center" data-id="element-1186">
                      <div className="flex justify-center mb-1" data-id="element-1187">
                        <div className="flex space-x-1" data-id="element-1188">
                          {Array(Math.min(3, goal.visionBoardData?.thumbnail?.elementCount || 0)).fill(0).map((_, i) => <div key={i} className="w-2 h-2 rounded-full bg-green-400" data-id="element-1189"></div>)}
                        </div>
                      </div>
                      <p className="text-gray-600 text-xs" data-id="element-1190">
                        {goal.visionBoardData?.thumbnail?.elementCount || 0}{' '}
                        elements
                      </p>
                    </div>
                  </div>
                  <div className="text-gray-500 text-xs flex justify-between" data-id="element-1191">
                    <span data-id="element-1192">
                      Created: {new Date(goal.createdAt).toLocaleDateString()}
                    </span>
                    <span data-id="element-1193">
                      {goal.mentorFeedback.length > 0 && <div className="flex items-center" data-id="element-1194">
                          <MessageSquareIcon size={12} className="mr-1 text-purple-500" data-id="element-1195" />
                          {goal.mentorFeedback.length}
                        </div>}
                    </span>
                  </div>
                </div>}
              <div className="flex justify-between items-center mt-2" data-id="element-1196">
                <div className="text-sm text-gray-500" data-id="element-1197">
                  {goal.type === 'pride' && <span className="flex items-center" data-id="element-1198">
                      <TargetIcon size={14} className="mr-1 text-purple-500" data-id="element-1199" />
                      PRIDE Framework
                    </span>}
                  {goal.type === 'note' && <span className="flex items-center" data-id="element-1200">
                      <FileTextIcon size={14} className="mr-1 text-blue-500" data-id="element-1201" />
                      Note Memo
                    </span>}
                  {goal.type === 'visionBoard' && <span className="flex items-center" data-id="element-1202">
                      <LayoutIcon size={14} className="mr-1 text-green-500" data-id="element-1203" />
                      Vision Board
                    </span>}
                </div>
                <button className="text-purple-600 hover:text-purple-800 text-sm flex items-center" data-id="element-1204">
                  View Details
                  <ChevronRightIcon size={16} className="ml-1" data-id="element-1205" />
                </button>
              </div>
            </div>
          </div>)}
        {filteredGoals.length === 0 && <div className="text-center py-10 bg-gray-50 rounded-lg" data-id="element-1206">
            <p className="text-gray-500" data-id="element-1207">No goals found matching your search</p>
            <button onClick={() => setShowGoalOptions(true)} className="mt-3 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700" data-id="element-1208">
              Create a new goal
            </button>
          </div>}
      </div>

      {showGoalDetails && <GoalDetailsModal data-id="element-1209" />}
      {showDeleteConfirmation && <DeleteConfirmationModal data-id="element-1210" />}
    </div>;
};
export default MenteeGoals;